<script setup>
import { onMounted, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';

import ContactsDetailsLayout from 'dashboard/components-next/Contacts/ContactsDetailsLayout.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ContactDetails from 'dashboard/components-next/Contacts/Pages/ContactDetails.vue';
import ContactNotes from 'dashboard/components-next/Contacts/ContactsSidebar/ContactNotes.vue';
import ContactHistory from 'dashboard/components-next/Contacts/ContactsSidebar/ContactHistory.vue';
import ContactMedia from 'dashboard/components-next/Contacts/ContactsSidebar/ContactMedia.vue';
import ContactMerge from 'dashboard/components-next/Contacts/ContactsSidebar/ContactMerge.vue';
import ContactCustomAttributes from 'dashboard/components-next/Contacts/ContactsSidebar/ContactCustomAttributes.vue';
import ContactTarefasTab from 'dashboard/components-next/Contacts/ContactsSidebar/ContactTarefasTab.vue';
import ContactProcessesTab from 'dashboard/components-next/Contacts/ContactsSidebar/ContactProcessesTab.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();

const contact = useMapGetter('contacts/getContactById');
const uiFlags = useMapGetter('contacts/getUIFlags');

const activeTab = ref('attributes');
const contactMergeRef = ref(null);

const isFetchingItem = computed(() => uiFlags.value.isFetchingItem);
const isMergingContact = computed(() => uiFlags.value.isMerging);
const isUpdatingContact = computed(() => uiFlags.value.isUpdating);

const selectedContact = computed(() => contact.value(route.params.contactId));

const showSpinner = computed(
  () => isFetchingItem.value || isMergingContact.value
);

const { t } = useI18n();

const isProcessosEnabled = computed(() => {
  return store.getters['accounts/isFeatureEnabledonAccount'](
    route.params.accountId,
    'processos_judiciais'
  );
});

const isTarefasEnabled = computed(() => {
  return store.getters['accounts/isFeatureEnabledonAccount'](
    route.params.accountId,
    'tarefas'
  );
});

const tabsList = computed(() => {
  const list = [
    { key: 'attributes', label: 'Atributos', icon: '📋' },
  ];
  if (isProcessosEnabled.value) {
    list.push({ key: 'processos', label: 'Processos', icon: '⚖️' });
  }
  if (isTarefasEnabled.value) {
    list.push({ key: 'prazos', label: 'Prazos', icon: '⏰' });
  }
  list.push(
    { key: 'history', label: 'Histórico', icon: '📜' },
    { key: 'notes', label: 'Notas', icon: '📝' },
    { key: 'media', label: 'Mídias', icon: '🖼️' },
    { key: 'merge', label: 'Mesclar', icon: '🔀' }
  );
  return list;
});

const goToContactsList = () => {
  if (window.history.state?.back || window.history.length > 1) {
    router.back();
  } else {
    router.push(`/app/accounts/${route.params.accountId}/contacts?page=1`);
  }
};

const fetchActiveContact = async () => {
  if (route.params.contactId) {
    await store.dispatch('contacts/show', { id: route.params.contactId });
    await store.dispatch(
      'contacts/fetchContactableInbox',
      route.params.contactId
    );
  }
};

const handleTabChange = (key) => {
  activeTab.value = key;
};

const fetchContactNotes = () => {
  const { contactId } = route.params;
  if (contactId) store.dispatch('contactNotes/get', { contactId });
};

const fetchContactConversations = () => {
  const { contactId } = route.params;
  if (contactId) store.dispatch('contactConversations/get', contactId);
};

const fetchAttributes = () => {
  store.dispatch('attributes/get');
};

const toggleContactBlock = async isBlocked => {
  const ALERT_MESSAGES = {
    success: {
      block: t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_SUCCESS_MESSAGE'),
      unblock: t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_SUCCESS_MESSAGE'),
    },
    error: {
      block: t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_ERROR_MESSAGE'),
      unblock: t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_ERROR_MESSAGE'),
    },
  };

  try {
    await store.dispatch(`contacts/update`, {
      ...selectedContact.value,
      blocked: !isBlocked,
    });
    useAlert(
      isBlocked ? ALERT_MESSAGES.success.unblock : ALERT_MESSAGES.success.block
    );
  } catch (error) {
    useAlert(
      isBlocked ? ALERT_MESSAGES.error.unblock : ALERT_MESSAGES.error.block
    );
  }
};

onMounted(() => {
  fetchActiveContact();
  fetchContactNotes();
  fetchContactConversations();
  fetchAttributes();
});
</script>

<template>
  <div
    class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-surface-1"
  >
    <ContactsDetailsLayout
      :button-label="$t('CONTACTS_LAYOUT.HEADER.SEND_MESSAGE')"
      :selected-contact="selectedContact"
      is-detail-view
      :show-pagination-footer="false"
      :is-updating="isUpdatingContact"
      @go-to-contacts-list="goToContactsList"
      @toggle-block="toggleContactBlock"
    >
      <div
        v-if="showSpinner"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>
      <ContactDetails
        v-else-if="selectedContact"
        :selected-contact="selectedContact"
        @go-to-contacts-list="goToContactsList"
      />
      
      <!-- HEADER DAS ABAS DA LATERAL - ROLÁVEL, ESPAÇOSO E SEM CORTAR TEXTOS -->
      <template #sidebarHeader>
        <div class="px-4 pt-5 pb-2">
          <div class="flex items-center gap-1.5 p-1.5 bg-n-alpha-black2 dark:bg-slate-800/60 rounded-xl border border-n-slate-4 dark:border-slate-700/70 overflow-x-auto whitespace-nowrap scrollbar-none shadow-2xs">
            <button
              v-for="tab in tabsList"
              :key="tab.key"
              type="button"
              class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer select-none shrink-0 border"
              :class="activeTab === tab.key
                ? 'bg-blue-600 border-blue-500 text-white shadow-xs'
                : 'bg-transparent border-transparent text-n-slate-11 dark:text-slate-400 hover:bg-n-alpha-black2 hover:text-n-slate-12'"
              @click="handleTabChange(tab.key)"
            >
              <span>{{ tab.icon }}</span>
              <span>{{ tab.label }}</span>
            </button>
          </div>
        </div>
      </template>

      <!-- CONTEÚDO DA ABA ATIVA -->
      <template #sidebar>
        <div
          v-if="isFetchingItem"
          class="flex items-center justify-center py-10 text-n-slate-11"
        >
          <Spinner />
        </div>
        <template v-else>
          <ContactCustomAttributes
            v-if="activeTab === 'attributes'"
            :selected-contact="selectedContact"
          />
          <ContactProcessesTab
            v-if="activeTab === 'processos'"
            :selected-contact="selectedContact"
          />
          <ContactTarefasTab
            v-if="activeTab === 'prazos'"
            :selected-contact="selectedContact"
          />
          <ContactNotes v-if="activeTab === 'notes'" />
          <ContactHistory v-if="activeTab === 'history'" />
          <ContactMedia v-if="activeTab === 'media'" />
          <ContactMerge
            v-if="activeTab === 'merge'"
            ref="contactMergeRef"
            :selected-contact="selectedContact"
            @go-to-contacts-list="goToContactsList"
            @reset-tab="handleTabChange('attributes')"
          />
        </template>
      </template>
    </ContactsDetailsLayout>
  </div>
</template>
